import { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { Toolbar } from "primereact/toolbar";
// FilterMatchMode constants for PrimeReact v6

import useFetchSubscriptions from "api/SubscriptionsResource/useFetchSubscriptions";
import { activeCurrencies } from "utils";
import Admin2 from "layouts/Admin2";
import useOverlayStore from "store/overlayStore";
import EditSubscription from "./EditSubscription";

const FilterMatchMode = {
	CONTAINS: "contains",
	STARTS_WITH: "startsWith",
	EQUALS: "equals",
};

const SubscriptionPlans = () => {
	const { setShowCreateSubscription } = useOverlayStore();
	// Pass visibility=all to include outbound subscriptions in admin view
	const { data: subs, isLoading } = useFetchSubscriptions({ visibility: "all" });
	const [filters, setFilters] = useState({
		global: { value: null, matchMode: FilterMatchMode.CONTAINS },
		name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
		category: { value: null, matchMode: FilterMatchMode.EQUALS },
	});

	const subscriptions = subs?.data || [];

	const categoryBodyTemplate = (rowData) => {
		const getCategoryColor = (category) => {
			switch (category?.toLowerCase()) {
				case "individual":
					return "danger";
				case "institution":
					return "primary";
				default:
					return "secondary";
			}
		};
		return (
			<Tag
				value={rowData.category}
				severity={getCategoryColor(rowData.category)}
				className="capitalize"
			/>
		);
	};

	const priceBodyTemplate = (rowData) => {
		const amount = rowData.price_per_seat ?? rowData.cost;
		const label = rowData.category === "institution" ? "per seat" : "per plan";

		return (
			<span className="font-semibold">
				{activeCurrencies.USD.label}${amount} <span className="text-xs text-gray-500">{label}</span>
			</span>
		);
	};

	const durationBodyTemplate = (rowData) => {
		return (
			<span>
				{rowData.duration} {rowData.time_unit}
			</span>
		);
	};

	const creditsBodyTemplate = (rowData) => {
		return <span className="font-medium text-akgreenest">{rowData.credits} stars</span>;
	};

	const maxBooksBodyTemplate = (rowData) => {
		if (rowData.category === "institution") {
			return (
				<span className="font-medium">
					{rowData.max_allocations_per_seat || rowData.max_number_of_books} allocations / seat
				</span>
			);
		}

		return <span className="font-medium">{rowData.max_number_of_books} books</span>;
	};

	const picksPolicyBodyTemplate = (rowData) => {
		if (rowData.category !== "institution") {
			return <span className="text-gray-400">Not applicable</span>;
		}

		return (
			<Tag
				value={rowData.picks_policy === "limited" ? "Limited" : "Unlimited"}
				severity={rowData.picks_policy === "limited" ? "warning" : "success"}
			/>
		);
	};

	const visibilityBodyTemplate = (rowData) => {
		const getVisibilityColor = (visibility) => {
			switch (visibility?.toLowerCase()) {
				case "internal":
					return "success";
				case "outbound":
					return "warning";
				default:
					return "secondary";
			}
		};
		return (
			<Tag
				value={rowData.visibility || "internal"}
				severity={getVisibilityColor(rowData.visibility)}
				className="capitalize"
			/>
		);
	};

	const actionBodyTemplate = (rowData) => {
		return (
			<div className="flex gap-2">
				<EditSubscription
					id={rowData.id}
					name={rowData.name}
					category={rowData.category}
					cost={rowData.cost}
					duration={rowData.duration}
					credits={rowData.credits}
					time_unit={rowData.time_unit}
					max_number_of_books={rowData.max_number_of_books}
					max_allocations_per_seat={rowData.max_allocations_per_seat}
					picks_policy={rowData.picks_policy}
					visibility={rowData.visibility}
				/>
			</div>
		);
	};

	const leftToolbarTemplate = () => {
		return (
			<div className="flex flex-col gap-2">
				<span className="text- font-semibold ">Subscription Plans ({subscriptions.length})</span>
			</div>
		);
	};

	const rightToolbarTemplate = () => {
		return (
			<Button
				label="Add plan"
				icon="pi pi-plus"
				className="p-button-success rounded-full text-sm"
				onClick={() => setShowCreateSubscription(true)}
			/>
		);
	};

	const header = (
		<div className="flex flex-col gap-4">
			<Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} className="mb-4" />
		</div>
	);

	if (isLoading) {
		return (
			<Admin2 fullWidth>
				<div className="flex justify-center items-center h-64">
					<div className="text-lg text-gray-600">Loading subscriptions...</div>
				</div>
			</Admin2>
		);
	}

	return (
		<Admin2 fullWidth>
			<div className="card">
				<DataTable
					value={subscriptions}
					// paginator
					rows={10}
					rowsPerPageOptions={[5, 10, 25, 50]}
					tableStyle={{ minWidth: "50rem" }}
					filters={filters}
					onFilter={(e) => setFilters(e.filters)}
					globalFilterFields={["name", "category", "cost"]}
					header={header}
					emptyMessage="No subscription plans found."
					className="p-datatable-sm text-sm font-sans "
					// paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
					// currentPageReportTemplate="Showing {first} to {last} of {totalRecords} subscription plans"
				>
					<Column
						field="name"
						header="Plan Name"
						// sortable
						filter
						filterPlaceholder="Search by name"
						style={{ minWidth: "200px" }}
					/>
					<Column
						field="category"
						header="Category"
						// sortable
						filter
						filterElement={
							<Dropdown
								value={filters.category.value}
								options={[
									{ label: "All Categories", value: null },
									...Array.from(new Set(subscriptions.map((sub) => sub.category))).map(
										(category) => ({
											label: category,
											value: category,
										})
									),
								]}
								onChange={(e) =>
									setFilters({
										...filters,
										category: { value: e.value, matchMode: FilterMatchMode.EQUALS },
									})
								}
								placeholder="Select Category"
								className="p-column-filter"
								showClear
							/>
						}
						body={categoryBodyTemplate}
						style={{ minWidth: "150px" }}
					/>
					<Column
						field="cost"
						header="Price"
						// sortable
						body={priceBodyTemplate}
						style={{ minWidth: "170px" }}
					/>
					<Column
						field="duration"
						header="Duration"
						// sortable
						body={durationBodyTemplate}
						style={{ minWidth: "120px" }}
					/>
					<Column
						field="credits"
						header="Credits"
						// sortable
						body={creditsBodyTemplate}
						style={{ minWidth: "120px" }}
					/>
					<Column
						field="max_number_of_books"
						header="Access Limit"
						// sortable
						body={maxBooksBodyTemplate}
						style={{ minWidth: "170px" }}
					/>
					<Column
						field="picks_policy"
						header="Picks Bank"
						body={picksPolicyBodyTemplate}
						style={{ minWidth: "140px" }}
					/>
					<Column
						field="visibility"
						header="Visibility"
						// sortable
						body={visibilityBodyTemplate}
						style={{ minWidth: "120px" }}
					/>
					<Column
						header="Actions"
						body={actionBodyTemplate}
						exportable={false}
						style={{ minWidth: "120px" }}
					/>
				</DataTable>
			</div>
		</Admin2>
	);
};

export default SubscriptionPlans;
